import { BoxGeometry, Mesh, Vector3, InstancedMesh, Matrix4, MeshBasicMaterial, Group } from 'three';
import { Scene2DConfig, CellGroup, RowGroup, ColumnGroup, DimGroup } from './scene2DConfig';
import { Scene2DMouse } from './scene2DMouse';
import { Dimension } from '../utils/dimension';
import { BaseClass } from 'dac-web-base';
import { Scene2DMaterials } from './scene2DMaterials';
import { Merger } from '../utils/merger';
import { Utils3d } from '../utils/utils3d';

export const DRAWING_TYPE = {
	CELL: 'cell',
	ROW: 'row',
	COLUMN: 'column',
	GENERAL_DIMENSION: 'general_dimension',
	NONE: 'none'
};

export class Scene2DUnit extends BaseClass {
	constructor( renderUnit ) {
		super();
		this.scene = null;
		this.controls = null;
		this.camera = null;
		this._gridData = {
			cells: new CellGroup(),
			rows: new RowGroup(),
			columns: new ColumnGroup(),
			dimensions: new DimGroup()
		};
		this.renderUnit = renderUnit;
		this.enabled = false;
		this._lastCellSelected = null;
	}

	async init( grid, container ) {

		this.container = container;
		this.grid = grid;

		// init scene config
		new Scene2DConfig().init( this );
		
		// init mouse events
		this._sceneMouse = new Scene2DMouse();
		const mouse = this._sceneMouse.init( this, container );
		mouse.subscribe( 'onObjectClicked', this.#onObjectClicked.bind( this ) );
		mouse.subscribe( 'onObjectDblClicked', this.#onObjectDblClicked.bind( this ) );
		mouse.subscribe( 'onFloorClicked', this.#onFloorClicked.bind( this ) );
		mouse.subscribe( 'onObjectHover', this.#onObjectHover.bind( this ) );
		mouse.subscribe( 'onObjectDrag', this.#onObjectDrag.bind( this ) );
		
		await this.initGrid();
	}
	
	setEnabled( enabled ) {
		this.enabled = enabled;
		if( !this._sceneMouse ) return;
		this._sceneMouse.setEnabled( enabled );
	}

	async initGrid() {

		//get full size of the grid
		const size = this.getGridFullSize();

		// generate grid
		this.#generateGrid( size, this.grid );
		
		// generate grid controls
		await this.#generateGridControls( this.grid );

		// generate general dimensions
		await this.#generateGeneralDimensions( size );

		Object.values( this._gridData ).forEach( g => {
			
			//add grid data to scene
			this.scene.add( g );

			// make performant
			Utils3d.makePerformant( g );

		} );

		Utils3d.fitOrthoCamera( this.scene, this.camera, this.container );

		this.renderUnit.render();
	}

	getGridFullSize() {
		const size = {
			width: 0,
			length: 0
		};

		//TOTAL WIDTH
		for( let i = 0; i < this.grid.numRows; i++ ){
			const cells = this.grid.getCellsInRow( i );

			//iterate per cell. Rome rows may have more cells than others
			let width = 0;
			for( let j = 0; j < cells.length; j++ ) width += cells[j].width;

			if( width > size.width ) size.width = width;
		}

		//TOTAL LENGTH
		for( let i = 0; i < this.grid.numColumns; i++ ){
			const cells = this.grid.getCellsInColumn( i );

			//iterate per cell. Rome rows may have more cells than others
			let length = 0;
			for( let j = 0; j < cells.length; j++ ) length += cells[j].length;
			if( length > size.length ) size.length = length;
		}

		return size;
	}

	#generateGrid( size, grid ){

		//clean grid
		this.#cleanGrid();

		//we create boxes for each cell. iterate per row
		let startPosZ = -size.length / 2.0;
		const cells = {};
		for( let i = 0; i < grid.numRows; i++ ){
			const row = grid.getCellsInRow( i );

			let startPosX = -size.width / 2.0;

			//iterate per cell
			for( let j = 0; j < row.length; j++ ){
				const cell = row[j];

				cell.position = new Vector3( startPosX + cell.width / 2.0, 0, startPosZ + cell.length / 2.0 );
				
				const key = `${cell.width}_${cell.length}_${cell.visible}`;
				if( !cells[key] ) cells[key] = [];
				const existing = cells[key].find( c => c.row === cell.row && c.column === cell.column );
				if( existing ) {
					existing.x = startPosX + cell.width / 2.0;
					existing.z = startPosZ + cell.length / 2.0;
					existing.visible = cell.visible;
					existing.selected = cell.selected;
				}
				else {
					cells[key].push( {
						cell: cell,
						x: startPosX + cell.width / 2.0,
						y: 0,
						z: startPosZ + cell.length / 2.0,
						visible: cell.visible
					} );
				}

				startPosX += cell.width;
			}

			startPosZ += row.length > 0 ? row[0].length : 0;
		}

		//create instance meshes with the same size
		this._gridData.cells.children = [];
		Object.keys( cells ).forEach( key => {
			const values = key.split( '_' );
			const width = parseFloat( values[0] );
			const length = parseFloat( values[1] );

			//draw visible cells
			const visibleCells = cells[key].filter( c => c.visible );
			const visibleMesh = this.#makeInstancedgeometry( width, length );
			const instances = new InstancedMesh( visibleMesh.geometry, visibleMesh.material, visibleCells.length );
			instances.cells = [];
			for( let i = 0; i < visibleCells.length; i++ ) {
				const c = visibleCells[i];
				c.cell.instanceId = i;
				c.cell.mesh2d = instances;
				instances.cells.push( c.cell );
				instances.setMatrixAt( i, new Matrix4().makeTranslation( c.x, c.y, c.z ) );
				//change color if selected
				instances.setColorAt( i, c.cell.selected ? Scene2DMaterials.CellSelectedMaterial.color : Scene2DMaterials.CellMaterial.color );
			}
			this._gridData.cells.add( instances );

			// draw invisible cells
			const invisibleCells = cells[key].filter( c => !c.visible );
			if( invisibleCells.length === 0 ) return;

			const invisibleMesh = visibleMesh.clone();
			//important to clone material too!
			invisibleMesh.material = visibleMesh.material.map( m => m.clone() );
			const invisibleInstances = new InstancedMesh( invisibleMesh.geometry, invisibleMesh.material, invisibleCells.length );
			invisibleInstances.cells = [];
			//make material invisible
			invisibleInstances.material.forEach( m => { m.transparent = true; m.opacity = 0; } );
			for( let i = 0; i < invisibleCells.length; i++ ) {
				const c = invisibleCells[i];
				c.cell.instanceId = i;
				c.cell.mesh2d = invisibleInstances;
				invisibleInstances.cells.push( c.cell );
				invisibleInstances.setMatrixAt( i, new Matrix4().makeTranslation( c.x, c.y, c.z ) );
			}
			this._gridData.cells.add( invisibleInstances );
		} );
	}

	#makeInstancedgeometry( width, length ) {
		const cellGeo = new BoxGeometry( width*0.95, 1, length*0.9 );
		const cell = new Mesh( cellGeo, Scene2DMaterials.CellMaterial.clone() );
		const cellOutlineGeo = new BoxGeometry( width, 0.5, length );
		const outline = new Mesh( cellOutlineGeo, new MeshBasicMaterial( { color: 0x000000 } ) );
		const group = new Group();
		group.add( cell );
		group.add( outline );
		return new Merger().merge( group );
	}

	async #generateGridControls( grid ) {

		const margin = 2;

		// iterate per row
		const textRotation = new Vector3( 0, -Math.PI / 2.0, 0 );
		for( let i = 0; i < grid.numRows; i++ ){
			const cells = grid.getCellsInRow( i );
			const cell = cells.find( c => c.length );

			const start = cell.position.clone();
			start.z += cell.length / 2.0;
			start.x -= cell.width / 2.0;
			start.y = 0;
			const end = cell.position.clone();
			end.z -= cell.length / 2.0;
			end.x -= cell.width / 2.0;
			end.y = 0;
			const textPoint = start.clone(); textPoint.x -= margin;

			//use max 4 decimals, and remove them if is equal to 0000
			const text = cell.length.toFixed(4).replace(/\.?0*$/,'');

			const d = await new Dimension().createLinear( start, end, textPoint, text, 1, 0x000000, textRotation, false, true );
			//add row object
			d.traverse( c => c.userData.row = {
				index: i,
				cells: cells
			} );
			this._gridData.rows.add( d );
		}

		//iterate per column
		textRotation.set( 0, 0, 0 );
		for( let i = 0; i < grid.numColumns; i++ ){
			const cells = grid.getCellsInColumn( i );
			const cell = cells.find( c => c.width );

			const start = cell.position.clone();
			start.x -= cell.width / 2.0;
			start.z -= cell.length / 2.0;
			const end = cell.position.clone();
			end.x += cell.width / 2.0;
			end.z -= cell.length / 2.0;
			const textPoint = start.clone(); textPoint.z -= margin;
			
			//use max 4 decimals, and remove them if is equal to 0000
			const text = cell.width.toFixed(4).replace(/\.?0*$/,'');

			const d = await new Dimension().createLinear( start, end, textPoint, text, 1, 0x000000, textRotation, false, true );
			//add column object
			d.traverse( c => c.userData.column = {
				index: i,
				cells: cells
			} );
			this._gridData.columns.add( d );
		}
	}

	async #generateGeneralDimensions( size ) {
		const margin = 10;
		const textRotation = new Vector3( 0, 0, 0 );

		//horizontal
		const start = new Vector3( -size.width / 2.0, 0, size.length / 2.0 );
		const end = new Vector3( size.width / 2.0, 0, size.length / 2.0 );
		const textPoint = start.clone(); 
		textPoint.z += margin;

		//use max 4 decimals, and remove them if is equal to 0000
		let text = size.width.toFixed(4).replace(/\.?0*$/,'');

		const d = await new Dimension().createLinear( start, end, textPoint, text, 1, 0x000000, textRotation );
		this._gridData.dimensions.add( d );

		//vertical
		textRotation.set( 0, -Math.PI / 2.0, 0 );
		start.set( size.width / 2.0, 0, -size.length / 2.0 );
		end.set( size.width / 2.0, 0, size.length / 2.0 );
		textPoint.copy( start );
		textPoint.x += margin;

		//use max 4 decimals, and remove them if is equal to 0000
		text = size.length.toFixed(4).replace(/\.?0*$/,'');

		const d2 = await new Dimension().createLinear( start, end, textPoint, text, 1, 0x000000, textRotation );
		this._gridData.dimensions.add( d2 );	
	}

	async #onObjectClicked( data ) {
		if( data.type === DRAWING_TYPE.CELL ) {
			const intersect = data.intersect.meshes[0];
			const instance = intersect.object;
			const cell = instance.cells[ intersect.instanceId ];
			//const cell = data.object && data.object.userData.cell ? data.object.userData.cell : null;		
			if( cell ) {
				const cells = [];
				if( data.e.shiftKey && this._lastCellSelected ) {
					this.#getCellRange( this._lastCellSelected, cell, cells );
					
				} else {
					cells.push( cell );
				}

				//do something with the cell
				this.selectCells( cells, !cell.selected );
				await this.trigger( 'onCellsSelected', { cells: [ cell ] } );
				return;
			}
		}
		if( data.type === DRAWING_TYPE.ROW ) {
			const obj3d = data.intersect.meshes[0].object;
			const row = obj3d.userData.row ? obj3d.userData.row : null;
			if( row && row.cells.length > 0 ) {
				const active = row.cells[0].selected;
				this.selectCells( row.cells, !active );
				await this.trigger( 'onCellsSelected', { cells: row.cells } );
				return;
			}
		}
		if( data.type === DRAWING_TYPE.COLUMN ) {
			const obj3d = data.intersect.meshes[0].object;
			const column = obj3d.userData.column ? obj3d.userData.column : null;
			if( column && column.cells.length > 0 ) {
				const active = column.cells[0].selected;
				this.selectCells( column.cells, !active );
				await this.trigger( 'onCellsSelected', { cells: column.cells } );
				return;		
			}
		}
		
		this.renderUnit.render();
	}

	#getCellRange( cell1, cell2, cells ) {
		//select all cells between the last selected cell and the current cell
		const row = cell1.row;
		const column = cell1.column;
		const lastRow = cell2.row;
		const lastColumn = cell2.column;
		for( let i = Math.min( row, lastRow ); i <= Math.max( row, lastRow ); i++ ) {
			for( let j = Math.min( column, lastColumn ); j <= Math.max( column, lastColumn ); j++ ) {
				const c = this.grid.getCell( i, j, true );
				if( c ) cells.push( c );
			}
		}
	}

	async #onObjectDblClicked( data ){

		//diselect all cells first
		this.#onFloorClicked();
		if( data.type === DRAWING_TYPE.ROW ) {
			const obj3d = data.intersect.meshes[0].object;
			const row = obj3d.userData.row ? obj3d.userData.row : null;
			if( row ) {
				await this.trigger( 'onRowLengthChange', row );
				return;
			}
		}
		if( data.type === DRAWING_TYPE.COLUMN ) {
			const obj3d = data.intersect.meshes[0].object;
			const column = obj3d.userData.column ? obj3d.userData.column : null;
			if( column ) {
				await this.trigger( 'onColumnWidthChange', column );
				return;		
			}
		}

		this.renderUnit.render();
	}

	async #onObjectDrag( data ) {
		if( data.type !== DRAWING_TYPE.CELL ) return;

		const intersect = data.intersect.meshes[0];
		const instance = intersect.object;
		const cell = instance.cells[ intersect.instanceId];		
		if( cell ) {
			//do something with the cell
			this.selectCells( cell, true );
			await this.trigger( 'onCellsSelected', { cells: [ cell ] } );
			return;
		}
	}

	async #onFloorClicked() {
		this.clearSelection();

		await this.trigger( 'onCellsSelected', { cells: [] } );

		this.renderUnit.render();
	}

	#onObjectHover( data ) {
		if(data.type === DRAWING_TYPE.CELL || data.type === DRAWING_TYPE.ROW || data.type === DRAWING_TYPE.COLUMN ) {
			this.container.style.cursor = 'pointer';
		}
	}

	selectCells( cells, select ) {
		const cs = cells instanceof Array ? cells : [ cells ];
		let lastCell = null;

		for( let i=0; i < cs.length; i++ ) {
			const cell = cs[i];
			if( !cell ) continue;
			
			if( !cell.visible ) {
				cell.selected = false;
				if( cell.mesh2d ) {
					if( typeof cell.instanceId === 'undefined' )
						cell.mesh2d.material = Scene2DMaterials.CellMaterial;
					else {
						cell.mesh2d.setColorAt( cell.instanceId, Scene2DMaterials.CellMaterial.color );
						cell.mesh2d.instanceColor.needsUpdate = true;
					}
					cell.mesh2d.needsUpdate = true;
					
				}
				continue;
			}
			
			cell.selected = select;
			
			if( cell.mesh2d ) {
				if( typeof cell.instanceId === 'undefined' )
					cell.mesh2d.material = cell.selected ? Scene2DMaterials.CellSelectedMaterial : Scene2DMaterials.CellMaterial;
				else {
					cell.mesh2d.setColorAt( cell.instanceId, cell.selected ? Scene2DMaterials.CellSelectedMaterial.color : Scene2DMaterials.CellMaterial.color );
					cell.mesh2d.instanceColor.needsUpdate = true; 
				}
				cell.mesh2d.needsUpdate = true;
			}

			lastCell = cell;
		}

		if( lastCell && lastCell.selected ) this._lastCellSelected = lastCell;

		this.renderUnit.render();
	}

	toScreenXY( position, camera, div) {
		
		const hw = div.clientWidth / 2.0;
		const hh = div.clientHeight / 2.0;

		camera.updateMatrixWorld();

		const pos2d = position.clone();
		pos2d.project( camera );
		
		var offset = this.#findOffset( div );

		pos2d.x = ( pos2d.x * hw ) + hw  + offset.left;
		pos2d.y = - ( pos2d.y * hh ) + hh  + offset.top;
	
		return pos2d;
	}

	#findOffset( html ) { 
		const pos = { left: 0, top: 0 };
		if ( !html.offsetParent ) return pos;  

		do  
		{ 
			pos.left += html.offsetLeft; 
			pos.top += html.offsetTop; 
		} while ( html === html.offsetParent ); 

		return pos;
	}

	#cleanGrid() {
		
		this._lastCellSelected = null;

		for( let i = 0; i < this.grid.numRows; i++ ) {
			const cells = this.grid.getCellsInRow( i );

			for( let j = 0; j < cells.length; j++ ) {
				if( cells[j].mesh2d ) {
					this.scene.remove( cells[j].mesh2d );
					Utils3d.dispose3d( cells[j].mesh2d, false );
					cells[j].mesh2d = null;
				}
			}
		}
		this.scene.remove( this._gridData.cells );
		Utils3d.dispose3d( this._gridData.cells );
		this._gridData.cells = new CellGroup();

		//clean row dimensions
		Utils3d.dispose3d( this._gridData.rows );
		this.scene.remove( this._gridData.rows );
		this._gridData.rows = new RowGroup();

		//clean column dimensions
		Utils3d.dispose3d( this._gridData.columns );
		this.scene.remove( this._gridData.columns );
		this._gridData.columns = new ColumnGroup();

		//clean general dimensions
		Utils3d.dispose3d( this._gridData.dimensions );
		this.scene.remove( this._gridData.dimensions );
		this._gridData.dimensions = new DimGroup();
	}

	clearSelection() {		
		//clear all the selected cells
		const cells = this.grid.getSelectedCells();
		this.selectCells( cells, false );
		this._lastCellSelected = null;
	}
}